import App from './assets.config.json';

import { Record, Asset, AssetRepository, RecordId, logError } from '@softools/softools-core';

import { OnlineEmbeddedApp } from '../embedded-application';
import { RecordPatch } from 'app/workspace.module/types';
import { Injectable } from '@angular/core';
import { AppCapabilities, ToolbarContext, DeleteRecordOptions } from 'app/types/application';
import { RecordSelection } from 'app/types/record-selection';
import { CallbackToolbarAction, ToolbarAction } from 'app/softoolscore.module/types/classes';
import { AppZone } from 'app/types/enums';
import { PermissionsService } from 'app/services/permissions.service';
import { IAppExtension } from 'app/types/app-extension';
import { FieldBase } from 'app/softoolsui.module/fields';
import { AppField } from 'app/types/fields/app-field';
import { FileDropComponent } from 'app/softoolsui.module/fields2/file-drop/file-drop.component';
import { InjectService } from 'app/services/locator.service';
import { ToastyService } from 'app/services/toasty.service';
import { AppModel, ReportModel } from 'app/mvc';

/**
 * Extension to create assets when files dropped on upload component
 */
class DropAssetAttachmentExtension implements IAppExtension<File> {

  public constructor(private app: AssetsApplication) {
  }

  @InjectService(AssetRepository)
  private readonly repository: AssetRepository;

  @InjectService(ToastyService)
  private readonly toasties: ToastyService;

  public async executeAsync(appModel: AppModel, _record, file: File): Promise<void> {
    if (this.isImageFile(file)) {
      // Upload file.  Can't use async here as file object gets cleared
      this.repository.createAssetAsync(file).then(() => {
        this.toasties.success(file.name, 'Created Asset');
        this.app.refresh().catch(e => logError(e, 'refresh'));
      }).catch(() => {
        // Display generic error toasty as response is "Ok"
        this.toasties.error('Error uploading Asset');
      });
    } else {
      this.toasties.error('Asset file must be an image type');
    }
  }

  private isImageFile(file: File) {
    switch (file.type) {
      case 'image/png':
      case 'image/gif':
      case 'image/jpg':
      case 'image/jpeg':
      case 'image/jpe':
      case 'image/bmp':
      case 'image/svg':
      case 'image/tiff':
      case 'image/svg+xml':
        return true;
      default:
        return false;
    }
  }
}

@Injectable({ providedIn: 'root' })
export class AssetsApplication extends OnlineEmbeddedApp<Asset> {

  private dropField: AppField;

  constructor(
    private repository: AssetRepository,
    private permissionsService: PermissionsService,
  ) {
    // NB This config is also used for image list assets - modifiy with care!
    super(App.App);

    this.dropField = this.getField('DropNewAsset');

    // Patch download field, json doesn't like property type
    const download = this.Reports[0].ListReportFields.find(f => f.FieldIdentifier === 'Uri');
    if (download) {
      download.Alignment = 'center';
    }
  }

  public override initialiseFieldComponent(component: FieldBase) {
    if (component.fieldModel.Identifier === this.dropField?.Identifier && component instanceof FileDropComponent) {
      component.fileDroppedExtension = new DropAssetAttachmentExtension(this);
    }
  }


  public toRecord(asset: Asset): Record {
    const record = asset && {
      ...asset,
      _id: asset.Id,
      AppIdentifier: this.Identifier,
      Uri: `/Api/AssetsDownload/${asset.Id}`,
      Hierarchy: '',
      EditableAccessForUser: false,
      CreatedDate: null,
      CreatedByUser: '',
      UpdatedDate: null,
      UpdatedByUser: '',
      QuickFilterSearchText: asset.Name,
      IsArchived: false,
    } as Record;
    return record;
  }

  public upsertAsync(_recordPatch: RecordPatch): Promise<Record> {
    throw new Error('Method not implemented.');
  }

  public override async totalCount(): Promise<number> {
    return this.repository.getCountAsync();
  }

  public override async refresh(): Promise<boolean> {
    return true;
  }

  public override async getApiViewRecordCount(selection: RecordSelection): Promise<number> {
    selection = new RecordSelection(selection); // clone to mutate
    delete selection.showArchived;
    const queryParams = selection.queryParamsForCount(true);
    return this.repository.getCountAsync(queryParams);
  }

  public override async getRecordByIdAsync(id: RecordId): Promise<Record> {
    const asset = await this.repository.getAssetAsync(id);
    return asset && this.toRecord(asset);
  }

  public override async getApiRecordRange(selection: RecordSelection): Promise<Array<Record>> {
    const queryParams = selection.queryParams({ stripBrackets: true, supressArchived: true });
    const assets = await this.repository.getAssetsAsync(selection.count, selection.start, queryParams);
    return assets.map(summary => this.toRecord(summary));
  }

  public override async getRecordCursor(selection: RecordSelection, cursor: string): Promise<Record> {
    const queryParams = selection.queryParams({ stripBrackets: true, supressArchived: true });
    const assets = await this.repository.getAssetsAsync(1, +cursor - 1, queryParams);
    return assets.map(summary => this.toRecord(summary)).pop();
  }

  public override async nativeGetAll(): Promise<Array<Record>> {
    const count = await this.repository.getCountAsync();
    const assets = await this.repository.getAssetsAsync(count, 0, {});
    return assets.map(a => this.toRecord(a));
  }

  public override async deleteRecordsAsync(deleteOptions: DeleteRecordOptions): Promise<boolean> {

    const ids: Array<RecordId> = deleteOptions.selection.SelectedRecordIds;

    await Promise.all(ids.map(id => this.repository.deleteAsync(id)));

    // Notify all as deleted (this will include any failures - can improve)
    ids.forEach(deletedId => {
      this.recordDeleted$.next({ app: this, recordIdentifier: deletedId });
    });

    return true;
  }

  public override getReportToolbarActionModel(reportModel: ReportModel, actions: Array<ToolbarAction>, context: ToolbarContext) {

    switch (context.zone) {
      case AppZone.listreport:
        actions.push(new CallbackToolbarAction('Delete', 'trash', async () => {
          await reportModel.delete();
        }));
        break;
      default:
        break;
    }

    // Refresh on all zones
    actions.push(new CallbackToolbarAction('Refresh', 'sync', async () => {
      if (await this.refresh()) {
        reportModel.reload();
      }
    }));
  }

  public override get capabilities(): AppCapabilities {
    return {
      canCreate: false,
      canEdit: false,
      canView: false,
      canSort: true,
      canGroup: false,  // server API currently doesn't support grouping.
      noSelectAll: true,    // select all & delete too risky (and no API support)
    };
  }

  public override get permissions() {
    return this.permissionsService.assetPermissions;
  }
}
